import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { useQuery } from '@apollo/react-hooks';
import { gql } from '@apollo/client';

import { useFlags } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import { token } from '@atlaskit/tokens';

import { LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import {
	Attribution,
	withTransparentErrorBoundary,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';

const i18n = defineMessages({
	flagTitle: {
		id: 'experiment-land-in-sample-space-xflow.flag.title',
		defaultMessage: 'Here’s some quick tips to get started',
		description: 'Title text informing the user of useful tips on Confluence ',
	},
	flagBody: {
		id: 'experiment-land-in-sample-space-xflow.flag.body',
		defaultMessage:
			'Here’s a page populated with example data to help you understand how to use Confluence.',
		description: 'Body text displayed in a flag describing the page for the user',
	},
});

interface Props {
	spaceKey?: string;
}

const SpaceOrPageFlagComponent: React.FC<Props> = ({ spaceKey }) => {
	const { showFlag } = useFlags();
	const { formatMessage } = useIntl();
	const { setOnboardingState } = useSetOnboardingState();

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState([...Object.values(LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS)]);

	const { hasSeenSampleContentFlag } = deserializeState(onboardingStateData);

	if (isUnauthorizedError(onboardingStateError)) {
		markErrorAsHandled(onboardingStateError);
	}

	// eslint-disable-next-line graphql-client-usage/graphql-generic-types
	const { data, error } = useQuery(
		gql`
			query SpaceOverviewFlagQuery($spaceKey: String) {
				space(key: $spaceKey) {
					type
				}
			}
		`,
		{
			variables: {
				spaceKey,
			},
			skip: !spaceKey || hasSeenSampleContentFlag || onboardingStateLoading || onboardingStateError,
		},
	);

	const spaceType = data?.space.type;

	if (isUnauthorizedError(error)) {
		markErrorAsHandled(error);
	}

	useEffect(() => {
		if (spaceType === 'xflow_sample_space' && !hasSeenSampleContentFlag) {
			showFlag({
				title: formatMessage(i18n.flagTitle),
				description: formatMessage(i18n.flagBody),
				icon: <CheckCircleIcon label="check" color={token('color.icon.accent.green')} />,
			});

			// Mark as dismissed as long as the user has seen the flag
			setOnboardingState({
				key: LAND_IN_SAMPLE_SPACE_ONBOARDING_STATE_KEYS.hasSeenSampleContentFlag,
				value: 'true',
			});
		}
	}, [showFlag, formatMessage, spaceType, hasSeenSampleContentFlag, setOnboardingState]);

	return <></>;
};

export const SpaceOverviewFlag = withTransparentErrorBoundary({
	attribution: Attribution.FLYWHEEL_ACTIVATION,
})(SpaceOrPageFlagComponent);
